import { UnitType } from "../../../types/FoundItemTypes";

interface Dimension {
    main?: number;
    sub?: number;
}

interface Dimensions {
    length: Dimension;
    width: Dimension;
    height: Dimension;
    weight: Dimension;
}

// Update the limits to support decimal values
export const IMPERIAL_LIMITS = {
    length: { sub: 99.99 }, // inches (0-99.99)
    width: { sub: 99.99 },
    height: { sub: 99.99 },
    weight: { sub: 99.99 }  // pounds (0-99.99)
};

export const METRIC_LIMITS = {
    length: { sub: 999.99 }, // cm (0-999.99)
    width: { sub: 999.99 },
    height: { sub: 999.99 },
    weight: { sub: 99.99 } // kg (0-99.99)
};

// Add validation functions
export const validateImperialValue = (type: keyof Dimensions, value: Dimension): Dimension => {
    // const limits = IMPERIAL_LIMITS[type];
    let { main, sub } = value;

    // Handle negative values
    main = Math.max(0, main ?? 0);
    sub = Math.max(0, sub ?? 0);

    // No conversion logic - allow any values
    return { main, sub };
};

export const validateMetricValue = (type: keyof Dimensions, value: Dimension): Dimension => {
    // const limits = METRIC_LIMITS[type];
    let { main, sub } = value;

    // Handle negative values
    main = Math.max(0, main ?? 0);
    sub = Math.max(0, sub ?? 0);

    // No conversion logic - allow any values
    return { main, sub };
};

export const convertToMetric = (dimensions: Dimensions | undefined) => {
    if (!dimensions) return {};
    const convertLength = (main: number, sub: number) => {
        // Convert total inches to centimeters
        // const totalInches = (main * 12) + sub;
        const totalInches = main + sub;
        const totalCentimeters = totalInches * 2.54;

        // Split into meters and centimeters
        const meters = Math.floor(totalCentimeters / 100);
        const centimeters = Math.round(totalCentimeters % 100);

        return validateMetricValue('length', { main: meters, sub: centimeters });
    };

    const convertWeight = (main: number, sub: number) => {
        // Convert total ounces to grams
        // const totalOunces = (main * 16) + sub;
        const totalOunces = main + sub;
        const totalGrams = totalOunces * 28.3495;

        // Split into kilograms and grams
        const kilograms = Math.floor(totalGrams / 1000);
        const grams = Math.round(totalGrams % 1000);

        return validateMetricValue('weight', { main: kilograms, sub: grams });
    };

    return {
        length: convertLength(dimensions.length.main || 0, dimensions.length.sub || 0),
        width: convertLength(dimensions.width.main || 0, dimensions.width.sub || 0),
        height: convertLength(dimensions.height.main || 0, dimensions.height.sub || 0),
        weight: convertWeight(dimensions.weight.main || 0, dimensions.weight.sub || 0)
    };
};

export const convertToImperial = (dimensions: Dimensions | undefined) => {
    if (!dimensions) return {
        length: { main: 0, sub: 0 },
        width: { main: 0, sub: 0 },
        height: { main: 0, sub: 0 },
        weight: { main: 0, sub: 0 }
    };
    const convertLength = (main: number, sub: number) => {
        // Convert total centimeters to inches
        const totalCentimeters = (main * 100) + sub;
        const totalInches = totalCentimeters / 2.54;

        // Split into feet and inches
        const feet = Math.floor(totalInches / 12);
        const inches = Math.round(totalInches % 12);

        return validateImperialValue('length', { main: feet, sub: inches });
    };

    const convertWeight = (main: number, sub: number) => {
        // Convert total grams to ounces
        const totalGrams = (main * 1000) + sub;
        const totalOunces = totalGrams / 28.3495;

        // Split into pounds and ounces
        const pounds = Math.floor(totalOunces / 16);
        const ounces = Math.round(totalOunces % 16);

        return validateImperialValue('weight', { main: pounds, sub: ounces });
    };

    return {
        length: convertLength(dimensions.length.main || 0, dimensions.length.sub || 0),
        width: convertLength(dimensions.width.main || 0, dimensions.width.sub || 0),
        height: convertLength(dimensions.height.main || 0, dimensions.height.sub || 0),
        weight: convertWeight(dimensions.weight.main || 0, dimensions.weight.sub || 0)
    };
};

export function convertDimensionsUnits(
    values: Dimensions,
    oldUnit: UnitType,
    newUnit: UnitType
): Dimensions {
    // Skip conversion if units are the same
    if (oldUnit === newUnit) {
        return { ...values };
    }

    const convert = (type: keyof Dimensions): Dimension => {
        // Get current values, defaulting to 0 if null/undefined
        const mainValue = values[type]?.main ?? 0;
        const subValue = values[type]?.sub ?? 0;

        if (type === "weight") {
            if (oldUnit === 1 && newUnit === 2) {
                // Imperial to Metric: lb → kg
                const totalLb = mainValue;
                const totalKg = totalLb * 0.45359237;
                // Custom rounding: if decimal part is .5 or higher, round up to next whole number
                const decimalPart = totalKg - Math.floor(totalKg);
                return { main: decimalPart >= 0.9 ? Math.ceil(totalKg) : parseFloat(totalKg.toFixed(2)), sub: 0 };
            } else if (oldUnit === 2 && newUnit === 1) {
                // Metric to Imperial: kg → lb
                const totalKg = mainValue;
                const totalLb = totalKg * 2.20462;
                // Custom rounding: if decimal part is .5 or higher, round up to next whole number
                const decimalPart = totalLb - Math.floor(totalLb);
                return { main: decimalPart >= 0.9 ? Math.ceil(totalLb) : parseFloat(totalLb.toFixed(2)), sub: 0 };
            }
        } else {
            // For dimensions (length, width, height)
            if (oldUnit === 1 && newUnit === 2) {
                // Imperial to Metric: inches → cm
                // Convert inches to cm (1 inch = 2.54 cm)
                // Custom rounding: if decimal part is .5 or higher, round up to next whole number
                const cmValue = subValue * 2.54;
                const decimalPart = cmValue - Math.floor(cmValue);
                return { main: 0, sub: decimalPart >= 0.9 ? Math.ceil(cmValue) : parseFloat(cmValue.toFixed(2)) };
            } else if (oldUnit === 2 && newUnit === 1) {
                // Metric to Imperial: cm → inches
                // Convert cm to inches (1 cm = 0.393701 inches)
                // Custom rounding: if decimal part is .5 or higher, round up to next whole number
                const inchValue = subValue * 0.393701;
                const decimalPart = inchValue - Math.floor(inchValue);
                return { main: 0, sub: decimalPart >= 0.9 ? Math.ceil(inchValue) : parseFloat(inchValue.toFixed(2)) };
            }
        }

        // Fallback (no conversion needed)
        return { main: mainValue, sub: subValue };
    };

    return {
        length: convert("length"),
        width: convert("width"),
        height: convert("height"),
        weight: convert("weight"),
    };
}

// Get unit labels based on measurement type
export const getUnitLabels = (type: "length" | "width" | "height" | "weight", unit: UnitType) => {
    if (unit === 1) {
        return type === "weight" ? ["lb", ""] : ["", "in"];
    }
    return type === "weight" ? ["kg", ""] : ["", "cm"];
};

// Get single unit label based on measurement type
export const getUnitLabel = (type: "length" | "width" | "height" | "weight", unit: UnitType) => {
    if (unit === 1) {
        return type === "weight" ? "lb" : "in";
    }
    return type === "weight" ? "kg" : "cm";
};

export const validateNumber = (value: string | number | null): number | null => {
    if (value === null || value === "") return null;
    const num = typeof value === "string" ? parseFloat(value) : value;
    return isNaN(num) ? null : num;
};

export const getErrorMessage = (type: string) => {
    switch (type) {
        case "length":
            return "Please enter length";
        case "width":
            return "Please enter width";
        case "height":
            return "Please enter height";
        case "weight":
            return "Please enter weight";
        default:
            return "Please enter value";
    }
};


export const conversionFactors = {
    in: "In",
    cm: "Cm",
    lb: "Lb",
    kg: "Kg"
}